/**
 * NexusCodeAuth — Página de autenticação do NEXUS CODE.
 *
 * UX: Emergent-style split layout
 *   Left  (40%) : Logo + headline + form (email/password + botão)
 *   Right (60%) : Social proof, stats, preview da app
 */
import React, { useState } from "react";
import { ncAuth, ncToken } from "./nexuscodeApi";

const API_BASE = process.env.REACT_APP_API_URL || "https://nexuscode-backend.onrender.com";

const T = {
  bg:       "#030407",
  surface:  "#08090f",
  border:   "#16192a",
  accent:   "#7c3aed",
  accentL:  "#a78bfa",
  txt:      "#f1f5f9",
  muted:    "#64748b",
  dim:      "#1e2335",
  error:    "#f87171",
  success:  "#34d399",
  input:    "#0d0f1c",
};

export default function NexusCodeAuth({ onAuth, onBack }) {
  const [mode,     setMode]     = useState("login");   // "login" | "register"
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    let result;
    if (mode === "login") {
      result = await ncAuth.login(email, password);
    } else {
      if (!name.trim()) { setError("Nome obrigatório"); setLoading(false); return; }
      result = await ncAuth.register(email, password, name);
    }

    setLoading(false);
    if (result.error) { setError(result.error); return; }
    ncToken.set(result.data.token);
    onAuth?.(result.data);
  }

  return (
    <div style={{
      display: "flex", minHeight: "100vh", fontFamily: "'Manrope', system-ui, sans-serif",
      background: T.bg,
    }}>
      {/* ── Left: Auth form ─────────────────────────────────────── */}
      <div style={{
        width: "40%", minWidth: 360, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", padding: "48px 40px",
        borderRight: `1px solid ${T.border}`,
      }}>
        {/* Back to landing */}
        {onBack && (
          <button onClick={onBack} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#64748b", fontSize: 13, display: "flex", alignItems: "center",
            gap: 4, marginBottom: 24, padding: 0,
          }}>
            ← Voltar
          </button>
        )}

        {/* Logo */}
        <div style={{ marginBottom: 36, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            marginBottom: 8,
          }}>
            <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="ncBg" x1="0" y1="0" x2="38" y2="38" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#2d0a6e"/>
                  <stop offset="100%" stopColor="#7c3aed"/>
                </linearGradient>
                <filter id="ncGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="1.2" result="blur"/>
                  <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
              </defs>
              {/* Background */}
              <rect width="38" height="38" rx="11" fill="url(#ncBg)"/>
              {/* N strokes — nexus nodes connected */}
              <line x1="10" y1="10" x2="10" y2="28" stroke="white" strokeWidth="2.6" strokeLinecap="round" filter="url(#ncGlow)"/>
              <line x1="28" y1="10" x2="28" y2="28" stroke="white" strokeWidth="2.6" strokeLinecap="round" filter="url(#ncGlow)"/>
              <line x1="10" y1="10" x2="28" y2="28" stroke="#c4b5fd" strokeWidth="2.2" strokeLinecap="round" filter="url(#ncGlow)"/>
              {/* Corner nodes — nexus points */}
              <circle cx="10" cy="10" r="2.8" fill="#e9d5ff"/>
              <circle cx="10" cy="28" r="2.8" fill="#e9d5ff"/>
              <circle cx="28" cy="10" r="2.8" fill="#e9d5ff"/>
              <circle cx="28" cy="28" r="2.8" fill="#e9d5ff"/>
              {/* Mid-diagonal pulse dot */}
              <circle cx="19" cy="19" r="1.6" fill="#a78bfa" opacity="0.8"/>
            </svg>
            <span style={{ fontSize: 22, fontWeight: 800, color: T.txt, letterSpacing: "-0.04em" }}>
              NEXUS CODE
            </span>
          </div>
          <p style={{ fontSize: 14, color: T.muted, marginTop: 4, lineHeight: 1.5 }}>
            {mode === "login"
              ? "Bem-vindo de volta. Continua a construir."
              : "Cria a tua conta. É grátis para começar."}
          </p>
        </div>

        {/* ── Social login ── */}
        <div style={{ width: "100%", maxWidth: 340, marginBottom: 20 }}>
          {/* GitHub */}
          <button type="button"
            onClick={() => window.location.href = `${API_BASE}/nexuscode/auth/oauth/github`}
            style={socialBtn("#161b22", "#30363d", "#f0f6fc")}
          >
            <GitHubIcon />
            Continuar com GitHub
          </button>

          {/* Google */}
          <button type="button"
            onClick={() => window.location.href = `${API_BASE}/nexuscode/auth/oauth/google`}
            style={{ ...socialBtn("#fff", "#dadce0", "#3c4043"), marginTop: 10 }}
          >
            <GoogleIcon />
            <span style={{ color: "#3c4043" }}>Continuar com Google</span>
          </button>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 20, marginBottom: 4 }}>
            <div style={{ flex: 1, height: 1, background: T.border }} />
            <span style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap" }}>ou continua com email</span>
            <div style={{ flex: 1, height: 1, background: T.border }} />
          </div>
        </div>

        <form onSubmit={submit} style={{ width: "100%", maxWidth: 340 }}>
          {mode === "register" && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Nome</label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="O teu nome"
                style={inputStyle(T)} required
              />
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="dev@exemplo.com"
              style={inputStyle(T)} required
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Password</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder={mode === "register" ? "Mínimo 8 caracteres" : "••••••••"}
              style={inputStyle(T)} required
            />
          </div>

          {error && (
            <div style={{
              marginBottom: 14, padding: "10px 14px",
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)",
              borderRadius: 8, color: T.error, fontSize: 13,
            }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            width: "100%", padding: "13px 0", background: loading ? "rgba(124,58,237,0.5)" : T.accent,
            border: "none", borderRadius: 10, color: "#fff", fontSize: 15, fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
            letterSpacing: "-0.01em", transition: "background 0.15s",
          }}>
            {loading
              ? (mode === "login" ? "A entrar..." : "A criar conta...")
              : (mode === "login" ? "Entrar →" : "Criar conta grátis →")}
          </button>

          <div style={{ marginTop: 16, textAlign: "center", fontSize: 13, color: T.muted }}>
            {mode === "login" ? (
              <>Ainda não tens conta?{" "}
                <button type="button" onClick={() => { setMode("register"); setError(null); }}
                  style={linkBtn}>Regista-te grátis</button>
              </>
            ) : (
              <>Já tens conta?{" "}
                <button type="button" onClick={() => { setMode("login"); setError(null); }}
                  style={linkBtn}>Entrar</button>
              </>
            )}
          </div>
        </form>

        <p style={{ marginTop: 32, fontSize: 11, color: T.dim, textAlign: "center" }}>
          Ao continuar, aceitas os <span style={{ color: T.muted }}>Termos de Serviço</span>{" "}
          e a <span style={{ color: T.muted }}>Política de Privacidade</span>.
        </p>
      </div>

      {/* ── Right: Social proof ─────────────────────────────────── */}
      <div style={{
        flex: 1, background: "linear-gradient(135deg, #0a0b14 0%, #0f0d1f 50%, #0a1020 100%)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: 48, position: "relative", overflow: "hidden",
      }}>
        {/* Grid decorativo */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: "linear-gradient(#a78bfa 1px, transparent 1px), linear-gradient(90deg, #a78bfa 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />

        <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 480 }}>
          <div style={{
            display: "inline-block", background: "rgba(124,58,237,0.15)",
            border: "1px solid rgba(124,58,237,0.3)", borderRadius: 20,
            padding: "6px 16px", fontSize: 12, fontWeight: 700, color: T.accentL,
            marginBottom: 24, letterSpacing: "0.06em", textTransform: "uppercase",
          }}>
            Powered by Claude Sonnet
          </div>

          <h1 style={{
            fontSize: 42, fontWeight: 900, color: T.txt, lineHeight: 1.15,
            letterSpacing: "-0.04em", marginBottom: 16,
          }}>
            Constrói qualquer app<br/>
            <span style={{ color: T.accentL }}>com IA no teu repo</span>
          </h1>

          <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.6, marginBottom: 40 }}>
            Conecta o teu GitHub. Descreve o que queres.<br/>
            O agente lê, escreve e commita — tu só approvas.
          </p>

          {/* Stats */}
          <div style={{ display: "flex", gap: 24, justifyContent: "center", marginBottom: 40 }}>
            {[
              { n: "50+",    label: "frameworks suportados" },
              { n: "< 30s",  label: "tempo médio por task" },
              { n: "100%",   label: "teu código, teu repo" },
            ].map(({ n, label }) => (
              <div key={n} style={{
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 12, padding: "16px 20px", textAlign: "center",
              }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: T.accentL, letterSpacing: "-0.03em" }}>{n}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Terminal mockup */}
          <div style={{
            background: "#050608", border: "1px solid #1a1d2e", borderRadius: 14,
            overflow: "hidden", textAlign: "left",
          }}>
            <div style={{
              padding: "10px 16px", borderBottom: "1px solid #1a1d2e",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#f87171", display: "inline-block" }} />
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fbbf24", display: "inline-block" }} />
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#34d399", display: "inline-block" }} />
              <span style={{ fontSize: 11, color: T.muted, marginLeft: 8 }}>nexuscode — workspace</span>
            </div>
            <div style={{ padding: "16px 20px", fontSize: 12.5, lineHeight: 1.7, color: "#8b9ec7" }}>
              <div><span style={{ color: T.accentL }}>você:</span> Adiciona autenticação JWT ao backend FastAPI</div>
              <div style={{ marginTop: 8 }}><span style={{ color: T.success }}>agente:</span> A ler auth.py e routes/users.py...</div>
              <div style={{ color: "#64748b" }}>✓ read_file(backend/auth.py)</div>
              <div style={{ color: "#64748b" }}>✓ read_file(backend/routes/users.py)</div>
              <div style={{ marginTop: 4 }}><span style={{ color: T.success }}>agente:</span> Proposta pronta. Aprovas o diff?</div>
              <div style={{ color: "#fbbf24", marginTop: 4 }}>⏳ Aguardando aprovação...</div>
              <div style={{ color: T.success, marginTop: 4 }}>✅ Commitado em branch: nexus/feat-jwt</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#64748b",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
};

function inputStyle(T) {
  return {
    width: "100%", boxSizing: "border-box",
    background: T.input, border: `1px solid ${T.border}`, borderRadius: 8,
    color: T.txt, fontSize: 14, padding: "10px 14px", outline: "none",
    fontFamily: "inherit",
  };
}

const linkBtn = {
  background: "none", border: "none", color: "#a78bfa",
  cursor: "pointer", fontSize: 13, padding: 0, fontFamily: "inherit",
};

function socialBtn(bg, border, color) {
  return {
    width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
    gap: 10, padding: "11px 0",
    background: bg, border: `1px solid ${border}`, borderRadius: 10,
    color, fontSize: 14, fontWeight: 600, cursor: "pointer",
    fontFamily: "inherit", transition: "opacity 0.15s",
  };
}

function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
